// Reading a report body, in one place.
//
// Report content reaches the UI in several shapes and all of them are
// legitimate:
//   * an already-parsed OBJECT      — what the backend returns now
//   * a JSON string                 — how older reports were stored
//   * that same JSON fenced in ```json … ``` — the model's habit, sometimes
//     stored verbatim
//   * genuine prose / markdown      — report types that were never JSON
//   * the literal "[GENERATING]"    — still being written
//
// There used to be THREE hand-rolled readers for this (ReportsPage's
// isReportValid and isReportComplete, and InteractiveReportView's
// cleanJsonString) and they each guarded differently. When the backend switched
// from strings to objects, two of them happened to check `typeof` and survived;
// the third guarded with `if (!str)`, an object is truthy, and it threw
// "str.replace is not a function" during render — taking every unlocked report
// on the site down through the error boundary for about a week. The app was
// unaffected because its parser tolerated both shapes.
//
// So the question "what is this report body?" is answered here, once. Adding a
// new shape means changing one function, not finding three.

export type ReportContent = string | Record<string, any> | null | undefined;

export type ReportRead =
  /** Still generating; not an error, just not ready. */
  | { kind: "generating" }
  /** Nothing there at all. */
  | { kind: "empty" }
  /** Structured report, whether it arrived parsed or as JSON text. */
  | { kind: "object"; data: Record<string, any> }
  /** A report type that is genuinely prose, not JSON. */
  | { kind: "prose"; text: string }
  /** Looked like JSON and was not — a truncated or corrupt generation. */
  | { kind: "invalid" };

const GENERATING = "[GENERATING]";

/**
 * Remove ```json / ```markdown fences.
 *
 * Takes `unknown` rather than `string` deliberately: typing this as `string`
 * is exactly what let an object reach `.replace()` with the compiler silent.
 */
export function stripFences(value: unknown): string {
  if (typeof value !== "string" || !value) return "";
  return value
    .replace(/^```(json|markdown)?\s*/i, "")
    .replace(/```\s*$/, "")
    .trim();
}

/** Classify a report body. The single source of truth for its shape. */
export function readReport(content: ReportContent): ReportRead {
  if (content === GENERATING) return { kind: "generating" };
  if (content === null || content === undefined || content === "") {
    return { kind: "empty" };
  }

  // Already an object: the common case now. Checked FIRST so no string work
  // can ever run against it.
  if (typeof content === "object") {
    return { kind: "object", data: content as Record<string, any> };
  }

  if (typeof content !== "string") return { kind: "invalid" };

  // Fences come off before deciding whether this looks like JSON. The old
  // isReportValid tested startsWith("{") on the RAW string, so a fenced report
  // began with a backtick and was misfiled as prose — harmless there, but the
  // kind of divergence that made three readers disagree.
  const cleaned = stripFences(content);
  if (!cleaned) return { kind: "empty" };

  if (cleaned.startsWith("{") || cleaned.startsWith("[")) {
    try {
      const parsed = JSON.parse(cleaned);
      if (parsed && typeof parsed === "object") {
        return { kind: "object", data: parsed };
      }
      return { kind: "invalid" };
    } catch {
      return { kind: "invalid" }; // truncated / corrupt generation
    }
  }

  return { kind: "prose", text: cleaned };
}

/**
 * Is this something we can show at all?
 *
 * Prose counts: several report types are markdown and were never JSON. A body
 * carrying an `error` key does not — that is a failed generation, and showing
 * it as "Generated" is how a user ends up paying for nothing.
 */
export function isReportValid(content: ReportContent): boolean {
  const r = readReport(content);
  if (r.kind === "prose") return true;
  if (r.kind !== "object") return false;
  return !r.data.error;
}

/**
 * Is this the FULL structured report the renderer expects?
 *
 * Stricter than valid on purpose: a truncated generation can parse cleanly and
 * still be missing whole sections, and must not be shown or charged for.
 * Mirrors the app's _isReportBroken.
 */
export function isReportComplete(content: ReportContent): boolean {
  const r = readReport(content);
  if (r.kind !== "object") return false;
  return Boolean(r.data.overview && r.data.guidance && r.data.timeline);
}
